/**
 * Web implementation of HelpPageService
 * Integrates with restaurantStore for contact info and browser APIs for phone calls
 * Submits feedback to Flask backend at /customer-feedback
 */

import { useRestaurantStore, useOrderStore, useAuthStore, type Order } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import type {
  IHelpPageService,
  HelpFormData,
  HelpSubmissionResult,
  RestaurantContactInfo
} from '../../contracts'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Map internal form types to API category values (matching Flutter implementation)
 */
function mapFormTypeToCategory(type: HelpFormData['type']): string {
  switch (type) {
    case 'order-issue':
      return 'order_issue'
    case 'app-issue':
      return 'application_issue'
    case 'feedback':
      return 'feedback'
    default:
      return 'feedback'
  }
}

interface FeedbackResponse {
  reference_id?: string
}

interface ApiErrorShape {
  originalError?: {
    response?: {
      data?: { error?: string }
      status?: number
    }
  }
}

/**
 * Web-specific help page service implementation
 * Uses restaurantStore for restaurant data and browser APIs for phone calls
 */
export class WebHelpPageService implements IHelpPageService {
  private restaurantStore = useRestaurantStore.getState()

  /**
   * Submit a help request to the Flask backend
   * Posts to /customer-feedback endpoint matching Flutter implementation
   */
  async submitHelpRequest(formData: HelpFormData): Promise<HelpSubmissionResult> {
    try {
      // Get user info from auth store
      const authStore = useAuthStore.getState()
      const user = authStore.user

      // Get restaurant/business info
      const restaurant = this.restaurantStore.currentRestaurant

      // Build API payload matching Flutter implementation
      const payload: Record<string, string> = {
        category: mapFormTypeToCategory(formData.type),
        business_name: restaurant?.name || 'Restaurant',
        name: user ? `${user.firstName} ${user.lastName}`.trim() : 'Guest',
        email: user?.email || '',
        phone_number: user?.phoneNumber || '',
        description: formData.message,
      }

      // Only add order_id if provided (for order issues)
      if (formData.orderId) {
        payload['order_id'] = formData.orderId
      }

      const logger = safeGetLogger()
      logger?.debug('Submitting feedback', {
        url: `${apiClient.getBaseURL()}/customer-feedback`,
        category: payload.category,
        hasAuth: !!user,
      })

      const responseData = await apiClient.post<FeedbackResponse>('/customer-feedback', payload)

      const logger4 = safeGetLogger()
      logger4?.debug('Submission successful')
      return {
        success: true,
        referenceId: responseData.reference_id || `HELP-${Date.now()}`,
      }
    } catch (error) {
      const axiosResponse = (error as ApiErrorShape)?.originalError?.response
      const responseError = axiosResponse?.data?.error
      const status = axiosResponse?.status

      let errorMessage: string
      if (responseError) {
        errorMessage = responseError
      } else if (status) {
        errorMessage = `Request failed with status ${status}`
      } else {
        errorMessage = error instanceof Error ? error.message : 'Network error. Please try again.'
      }

      const logger = safeGetLogger()
      logger?.error('Error submitting feedback', { error: errorMessage })
      return {
        success: false,
        errorMessage,
      }
    }
  }

  /**
   * Get restaurant contact information from the current location
   */
  async getRestaurantContactInfo(): Promise<RestaurantContactInfo | null> {
    const location = this.restaurantStore.currentLocation
    const restaurant = this.restaurantStore.currentRestaurant

    if (!location?.phone) {
      return null
    }

    return {
      name: restaurant?.name || location.name || 'Restaurant',
      phoneNumber: location.phone
    }
  }

  /**
   * Initiate a phone call using the tel: protocol
   */
  callRestaurant(phoneNumber: string): void {
    window.open(`tel:${phoneNumber}`)
  }

  /**
   * Get user's order history for order issue selection
   * Reads from the orderStore which is populated when orders are placed
   * Note: Order history is added to store when user places orders via checkout
   * Returns orders sorted by createdAt descending (most recent first)
   */
  async getOrderHistory(): Promise<Order[]> {
    // Get current user from auth store
    const authStore = useAuthStore.getState()
    const user = authStore.user

    const logger = safeGetLogger()
    logger?.debug('getOrderHistory called')
    logger?.debug('Current user', { userId: user?.id ?? 'not authenticated' })

    // If no authenticated user, return empty array
    if (!user?.id) {
      const logger2 = safeGetLogger()
      logger2?.debug('No authenticated user, returning empty order history')
      return []
    }

    // Get orders from the store
    // Orders are added to history when placed via checkout (addToHistory)
    const orderStore = useOrderStore.getState()
    const orders = orderStore.orderHistory || []

    const logger3 = safeGetLogger()
    logger3?.debug('Orders in store', { count: orders.length })

    // Sort by orderTime descending (most recent first)
    const sortedOrders = [...orders].sort((a, b) => {
      return new Date(b.orderTime).getTime() - new Date(a.orderTime).getTime()
    })

    const logger4 = safeGetLogger()
    logger4?.debug('Returning sorted orders', { count: sortedOrders.length })
    return sortedOrders
  }
}
